import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { Session } from '#src/shared/session';

import type { AiSummaries } from './aiSummaries';
import { getAiSummaries } from './aiSummaries';
import { isDecisionMaker } from './decisionMaker';
import { fetchPetitionDetailsExperiments } from './experiments';
import type { PetitionDetailsDataExperimentsQuery } from './experiments.graphql';
import { isFullAddressCollectionEnabled } from './fullAddressCollectionEnabled';
import { getFullAddressCollectionFilters } from './fullAddressCollectionFilters';
import { isGdprConsentProvided } from './gdprConsentProvided';
import { normalizePetitionDetailsShareCtaCopyExperiment } from './normalize/normalizePetitionDetailsShareCtaCopyExperiment';
import { normalizePetitionDetailsStickySignCtaExperiment } from './normalize/normalizePetitionDetailsStickySignCtaExperiment';
import { normalizePetitionPageAiSummaryCardsExperiment } from './normalize/normalizePetitionPageAiSummaryCardsExperiment';
import { normalizeSignFormAddressRemovalExperiment } from './normalize/normalizeSignFormAddressRemovalExperiment';
import { normalizeSignFormGdprRadioRemovalExperiment } from './normalize/normalizeSignFormGdprRadioRemovalExperiment';
import { normalizeSignFormLastNameInitialExperiment } from './normalize/normalizeSignFormLastNameInitialExperiment';
import type { PetitionViewerInfo } from './petitionViewerInfo';
import { getPetitionViewerInfo } from './petitionViewerInfo';
import { getRecentUserPetition } from './recentUserPetition';

type Experiments = PetitionDetailsDataExperimentsQuery['experiments'];
type PetitionDetailsShareCtaCopyExperiment = Experiments['petitionDetailsShareCtaCopyExperiment'];
export type PetitionDetailsShareCtaCopyExperimentVariation = PetitionDetailsShareCtaCopyExperiment['variation'];
export type PetitionDetailsStickySignCtaExperiment = Experiments['petitionDetailsStickySignCtaExperiment'];

export type PetitionDetailsPageUserData = PetitionViewerInfo & {
	userId: string | undefined;
	decisionMaker: boolean;
	gdprConsentProvided: boolean;
	fullAddressCollectionEnabled: boolean;
	starterDashboardPromptingBannerEnabled: boolean;
	// All experiment-derived properties live under `experiments` (rather than as top-level keys), even
	// primitive-valued ones, for consistency. Object-valued properties (like aiSummaries) MUST live here because
	// prefetchComponentUserData disallows nested/object-valued properties on prefetched user data except
	// `experiments`, to limit impact on server-caching (see CHANGE-95363, CHANGE-95416).
	experiments: Omit<
		Experiments,
		| 'signatureThermometerExperimentDesktop'
		| 'petitionPageStarterInHeaderExperiment'
		| 'petitionPageAiSummaryCardsExperiment'
	> & {
		aiSummaries: AiSummaries | null;
		signatureThermometerEnabled: boolean;
		petitionPageStarterInHeaderExperimentEnabled: boolean;
		petitionPageAiSummaryCardsExperimentEnabled: boolean;
	};
};

// oxlint-disable-next-line max-lines-per-function
export async function getPetitionDetailsPageUserData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		currentUserId,
		loginState,
		decisionMakerHash,
	}: {
		currentUserId: string | undefined;
		loginState: Session['loginState'] | undefined;
		decisionMakerHash?: string;
	},
): Promise<PetitionDetailsPageUserData> {
	const isGuest = loginState ? loginState === 'GUEST' : true;
	const [
		{ petitionId, ...petitionViewerInfo },
		aiSummaries,
		decisionMaker,
		gdprConsentProvided,
		recentPetition,
		fullAddressCollectionFilters,
		experiments,
	] = await Promise.all([
		getPetitionViewerInfo(utilityContext, slugOrId, currentUserId),
		getAiSummaries(utilityContext, { slugOrId }),
		!isGuest || decisionMakerHash
			? isDecisionMaker(utilityContext, { slugOrId, decisionMakerHash })
			: Promise.resolve(false),
		isGdprConsentProvided(utilityContext),
		isGuest ? Promise.resolve(undefined) : getRecentUserPetition(utilityContext),
		getFullAddressCollectionFilters(utilityContext, slugOrId),
		fetchPetitionDetailsExperiments(utilityContext),
	]);

	const {
		signatureThermometerExperimentDesktop,
		petitionPageStarterInHeaderExperiment,
		petitionDetailsShareCtaCopyExperiment,
		petitionDetailsStickySignCtaExperiment,
		petitionPageAiSummaryCardsExperiment,
		signFormGdprRadioRemovalExperiment,
		signFormLastNameInitialExperiment,
		signFormAddressRemovalExperiment,
		...experimentsRest
	} = experiments;

	// Deliberately sequential, not Promise.all: each normalizer that treats can call
	// treatExperiment(), which patches the session's tracking-data cookie via a read-modify-write
	// scoped to that single HTTP request/response. Running these concurrently would have each
	// request read the same pre-patch snapshot and race to write it back, so only the
	// last-applied response's patch would survive — silently dropping the other experiments from
	// `experiment_variations`. Sequencing them keeps each patch built on the previous one's write.
	const normalizedPetitionDetailsShareCtaCopyExperiment = await normalizePetitionDetailsShareCtaCopyExperiment({
		experiment: petitionDetailsShareCtaCopyExperiment,
		utilityContext,
	});

	const petitionPageAiSummaryCardsExperimentEnabled = await normalizePetitionPageAiSummaryCardsExperiment({
		experiment: petitionPageAiSummaryCardsExperiment,
		aiSummaries,
		utilityContext,
	});

	// Sync and non-treating, so it sits outside the sequential block above — see its docblock for why
	// treatment is deferred to the consuming hook.
	const normalizedPetitionDetailsStickySignCtaExperiment = normalizePetitionDetailsStickySignCtaExperiment({
		experiment: petitionDetailsStickySignCtaExperiment,
		utilityContext,
	});

	const normalizedSignFormAddressRemovalExperiment = normalizeSignFormAddressRemovalExperiment({
		experiment: signFormAddressRemovalExperiment,
		loginState,
		geoReach: fullAddressCollectionFilters.geoReach,
	});

	const treatedSignFormGdprRadioRemovalExperiment = await normalizeSignFormGdprRadioRemovalExperiment({
		experiment: signFormGdprRadioRemovalExperiment,
		gdprConsentProvided,
		gdprSignOptInEnabled: experiments.gdprSignOptInCopy.config.enabled,
		signed: petitionViewerInfo.signed,
		utilityContext,
	});

	const normalizedSignFormLastNameInitialExperiment = normalizeSignFormLastNameInitialExperiment({
		experiment: signFormLastNameInitialExperiment,
		signed: petitionViewerInfo.signed,
	});

	return {
		...petitionViewerInfo,
		userId: currentUserId,
		decisionMaker,
		gdprConsentProvided,
		fullAddressCollectionEnabled: isFullAddressCollectionEnabled({
			filters: fullAddressCollectionFilters,
			loginState,
		}),
		starterDashboardPromptingBannerEnabled: isStarterDashboardPromptingBannerEnabled({
			petitionId,
			ownPetition: petitionViewerInfo.ownPetition,
			recentPetition,
		}),
		experiments: {
			...experimentsRest,
			petitionDetailsShareCtaCopyExperiment: normalizedPetitionDetailsShareCtaCopyExperiment,
			petitionDetailsStickySignCtaExperiment: normalizedPetitionDetailsStickySignCtaExperiment,
			aiSummaries,
			signatureThermometerEnabled: signatureThermometerExperimentDesktop.variation === 'v1',
			petitionPageStarterInHeaderExperimentEnabled: petitionPageStarterInHeaderExperiment.variation === 'enabled',
			petitionPageAiSummaryCardsExperimentEnabled,
			signFormGdprRadioRemovalExperiment: treatedSignFormGdprRadioRemovalExperiment,
			signFormLastNameInitialExperiment: normalizedSignFormLastNameInitialExperiment,
			signFormAddressRemovalExperiment: normalizedSignFormAddressRemovalExperiment,
		},
	};
}

function isStarterDashboardPromptingBannerEnabled({
	petitionId,
	ownPetition,
	recentPetition,
}: {
	petitionId?: string;
	ownPetition: boolean;
	recentPetition: { id: string } | undefined;
}) {
	if (!recentPetition || !petitionId) return false;
	if (!ownPetition) return false;
	if (recentPetition.id !== petitionId) return false;
	return true;
}
